import type { IntegrationConnectionStatus } from "./company";

export type { IntegrationConnectionStatus };

export type FinanceProduct =
	| "access-financials"
	| "access-dimensions";
export type TimesheetProduct =
	| "access-time"
	| "access-focalpoint"
	| "access-financial-operations";
export type ExpenseProduct = "access-expense";
export type PlannerProduct = "access-planner" | "resource-scheduler";
export type PurchasingProduct =
	| "access-financial-operations-purchasing"
	| "access-focalpoint-purchasing";

export const FINANCE_PRODUCT_LABELS: Record<FinanceProduct, string> = {
	"access-financials": "Access Financials",
	"access-dimensions": "Access Dimensions",
};

export const TIMESHEET_PRODUCT_LABELS: Record<TimesheetProduct, string> = {
	"access-time": "Access Time",
	"access-focalpoint": "Access FocalPoint",
	"access-financial-operations": "Access Financial Operations",
};

export const EXPENSE_PRODUCT_LABELS: Record<ExpenseProduct, string> = {
	"access-expense": "Access Expense",
};

export const PLANNER_PRODUCT_LABELS: Record<PlannerProduct, string> = {
	"access-planner": "Access Scheduler",
	"resource-scheduler": "Resource Scheduler",
};

export const PURCHASING_PRODUCT_LABELS: Record<PurchasingProduct, string> = {
	"access-financial-operations-purchasing":
		"Access Financials Operations \\ Access Financials",
	"access-focalpoint-purchasing": "Access FocalPoint \\ Access Dimensions",
};

export interface SimpleIntegrationConfig {
	product:
		| FinanceProduct
		| TimesheetProduct
		| ExpenseProduct
		| PlannerProduct
		| PurchasingProduct
		| null;
	databaseId: string | null;
	/** Only used by purchasing — stores the matched Financials Operations instance ID */
	finOpsInstanceId?: string | null;
	/** Only used by purchasing — stores the matched FocalPoint instance ID (Access FocalPoint \ Access Dimensions) */
	focalpointInstanceId?: string | null;
	/** Dimensions finance instance ID, surfaced for the FocalPoint purchasing path */
	dimensionsInstanceId?: string | null;
	status: IntegrationConnectionStatus;
	lastSyncAt: string | null;
	lastFullSyncAt?: string | null;
	errorMessage?: string | null;
}

export interface CompanyWithIntegrations {
	id: number;
	name: string;
	code: string;
	status: "active" | "inactive";
	financeIntegration: SimpleIntegrationConfig;
	timesheetIntegration: SimpleIntegrationConfig;
	expenseIntegration: SimpleIntegrationConfig;
	plannerIntegration: SimpleIntegrationConfig;
	purchasingIntegration: SimpleIntegrationConfig;
	userCount: number;
}

export interface CompanyUser {
	id: string;
	name: string;
	email: string;
	role: string;
	photoUrl?: string;
	isDefault: boolean;
}

export interface EmployeeCompanyAccessRow {
	companyId: number;
	companyName: string;
	companyCode: string;
	companyStatus: "active" | "inactive";
	hasAccess: boolean;
	isDefault: boolean;
}

export interface UpdateIntegrationsPayload {
	finance?: {
		product: FinanceProduct | null;
		databaseId: string | null;
		databaseName?: string | null;
	};
	timesheet?: { product: TimesheetProduct | null; databaseId: string | null };
	expense?: { product: ExpenseProduct | null; databaseId: string | null };
	planner?: { product: PlannerProduct | null; databaseId: string | null };
	purchasing?: {
		product: PurchasingProduct | null;
		databaseId: string | null;
		finOpsInstanceId?: string | null;
		focalpointInstanceId?: string | null;
	};
}

export interface TestIntegrationResponse {
	success: boolean;
	message: string;
	testedAt: string;
}

export interface AvailableDatabase {
	id: string; // the value stored in databaseId
	label: string; // human-readable name shown in the dropdown
	environment?: string; // e.g. 'Production', 'Staging', 'UAT'
	databaseName?: string; // physical database name from Workspace API (e.g. 'FINEDC1B6BEF7D2E8D26D66EF')
}

export interface SyncLogEntry {
	id: number;
	integrationType: string;
	status: "started" | "completed" | "failed" | "partial" | "interrupted";
	triggerType:
		| "manual"
		| "manual-full-resync"
		| "auto-scheduled"
		| "webhook"
		| "system";
	startedAt: string;
	completedAt: string | null;
	recordsCreated: number;
	recordsUpdated: number;
	recordsSkipped: number;
	recordsFailed: number;
	summaryMessage: string | null;
	errorMessage: string | null;
	initiatedBy: string | null;
}

export interface SyncLogPage {
	items: SyncLogEntry[];
	totalCount: number;
	page: number;
	pageSize: number;
}
